import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import { DisabledHint, useToast } from '@/ui';
import { oauthService, type OAuthProvider, type OAuthProviders } from '@/core/services/authService';
import { query } from '@/theme/breakpoints';
import { SignUpForm } from './SignUpForm';
import { SignInForm } from './SignInForm';

export interface AuthCardProps {
  mode: 'signin' | 'signup';
  next: string | null;
}

// Solid Panel (not translucent ConsolePanel): the auth screens are full-canvas
// over the live WebGL backdrop, where translucency would bleed. No brackets —
// the card is large and the corner marks would read as clutter here.
/* There is no card. The form is a bare column on the page's own ground; the
   ruling around it is masked away by the FormColumn in AuthScene. Spacing is a
   stepped scale set by each block (4/8/12/20/24/32), not one uniform gap. */
const Column = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
  width: 100%;
`;

const Brand = styled.div`
  text-align: left;
  .name {
    font-family: ${({ theme }) => theme.font.display};
    font-size: ${({ theme }) => theme.fontSize.xl};
    font-weight: 700;
    line-height: 32px;
    letter-spacing: -0.01em;
    color: ${({ theme }) => theme.color.textPrimary};
  }
  .name b {
    /* Solid safety-red (matches the approved mockup). The former 3-colour triad
       gradient read muddy/violet at wordmark size, the 55% privacy stop dominated. */
    color: ${({ theme }) => theme.color.safety};
  }
  .tag {
    color: ${({ theme }) => theme.color.textMuted};
    font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 20px;
    margin-top: 4px;
  }
`;

/* Two words, not a segmented control. */
const ModeRow = styled.div`
  margin-top: 32px;
  display: flex;
  gap: 24px;
  @media ${query.mobile} {
    margin-top: 20px;
  }
`;

/* The active mode word carries the page's one structural accent: a 2px rule
   under the word only — the app's own active-tab device. State is also carried
   by weight and colour, so the rule is never the only signal. */
const Tab = styled(Link)<{ $active: boolean }>`
  padding: 0 0 8px;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.lg};
  line-height: 24px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ theme, $active }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  text-decoration: none;
  border-bottom: 2px solid ${({ theme, $active }) => ($active ? theme.color.accentCyan : 'transparent')};
  transition: color 120ms ease;

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

/* A word, not a divider with wings. */
const Or = styled.div`
  margin-top: 24px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  line-height: 20px;
  color: ${({ theme }) => theme.color.textFaint};
  text-align: left;
  @media ${query.mobile} {
    margin-top: 20px;
  }
`;

const Socials = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* A social button is a real navigation to the API, so it is an <a>. It wears
   the secondary Button recipe rather than being a <Button> with an onClick
   that sets window.location — a link that is a link keeps middle-click, copy
   address and the status bar honest. */
const socialLook = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  @media ${query.mobile} {
    height: 44px;
  }
`;
const SocialLink = styled.a`
  ${socialLook}
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.hairSoft};
  background: transparent;
  color: ${({ theme }) => theme.color.textPrimary};
  transition: background 120ms ease;
  &:hover { background: ${({ theme }) => theme.color.hover}; }
`;
/* The same shape when the server has no credentials for that provider: it is
   disabled WITH the reason on hover/focus (DisabledHint), which is the house
   rule — a control wired to nothing is a bug, a control that says why it is
   inert is information. */
const SocialDisabled = styled.span`
  ${socialLook}
  cursor: not-allowed;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: transparent;
  color: ${({ theme }) => theme.color.textMuted};
`;

const PROVIDER_LABEL: Record<OAuthProvider, string> = { google: 'Google', apple: 'Apple' };

/* What the callback's ?oauth_error= means, in a sentence. Unknown codes get the
   generic line rather than the raw code. */
const OAUTH_ERROR_TEXT: Record<string, string> = {
  denied: 'Sign-in was cancelled.',
  state_invalid: 'That sign-in link expired. Start again from this page.',
  token_invalid: 'The provider did not confirm your identity. Try again.',
  exchange_failed: 'The provider did not answer. Try again in a moment.',
  email_unverified: 'That provider has not confirmed your email address, so it cannot sign you in here.',
  email_missing: 'The provider did not share an email address, which an account needs.',
  account_sanctioned: 'This account cannot sign in.',
  account_conflict: 'Two sign-ups collided. Try once more.',
  provider_not_configured: 'That sign-in method is not set up on this server.',
};

const SwitchLine = styled.p`
  margin: 20px 0 0;
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  margin: 0;
  a { color: ${({ theme }) => theme.color.accentCyan}; font-weight: 600; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

export function AuthCard({ mode, next }: AuthCardProps) {
  const toast = useToast();
  const verb = mode === 'signup' ? 'Sign up' : 'Login';
  const [params, setParams] = useSearchParams();
  const [providers, setProviders] = useState<OAuthProviders | null>(null);
  // One-shot params written by the OAuth callback. Captured at mount, then
  // stripped from the address bar so a reload cannot replay the message or
  // leave a 2FA challenge sitting in the URL.
  const [oauthError] = useState(() => params.get('oauth_error'));
  const [initialChallenge] = useState(() => params.get('totp_challenge'));

  useEffect(() => {
    let live = true;
    void oauthService.providers().then((p) => {
      if (live) setProviders(p);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (!oauthError && !initialChallenge) return;
    if (oauthError) {
      toast(OAUTH_ERROR_TEXT[oauthError] ?? 'Sign-in with that provider did not complete. Try again.');
    }
    const cleaned = new URLSearchParams(params);
    cleaned.delete('oauth_error');
    cleaned.delete('totp_challenge');
    setParams(cleaned, { replace: true });
    // Runs once, on mount, for the params captured above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Column>
      <Brand>
        <div className="name">
          Tru<b>X</b>Social
        </div>
        <div className="tag">Speak Freely. Connect Boldly. Built for the Future.</div>
      </Brand>

      {/* Tabs — the canonical toggle; each mode renders exactly ONE link per destination */}
      <ModeRow>
        <Tab to="/signup" $active={mode === 'signup'} aria-current={mode === 'signup' ? 'page' : undefined}>
          Sign Up
        </Tab>
        <Tab to="/signin" $active={mode === 'signin'} aria-current={mode === 'signin' ? 'page' : undefined}>
          Login
        </Tab>
      </ModeRow>

      {mode === 'signup' ? (
        <SignUpForm next={next} />
      ) : (
        <SignInForm next={next} initialChallenge={initialChallenge} />
      )}

      <Or>
        <span>OR</span>
      </Or>

      {/* Google and Apple only. There is deliberately NO "with Email" button here:
          email IS the form directly above, so a button announcing it as "coming
          soon" told people the one route into the product did not exist yet. */}
      <Socials>
        {(['google', 'apple'] as const).map((provider) => {
          const label = `${verb} with ${PROVIDER_LABEL[provider]}`;
          if (providers?.[provider]) {
            return (
              <SocialLink key={provider} href={oauthService.startUrl(provider, next)}>
                {label}
              </SocialLink>
            );
          }
          const why =
            providers === null
              ? 'Checking which sign-in methods are available…'
              : `${PROVIDER_LABEL[provider]} sign-in isn't set up on this server yet.`;
          return (
            <DisabledHint key={provider} explanation={why} block>
              {(describedBy) => (
                <SocialDisabled role="link" aria-disabled="true" aria-describedby={describedBy} tabIndex={0}>
                  {label}
                </SocialDisabled>
              )}
            </DisabledHint>
          );
        })}
      </Socials>

      <SwitchLine>
        {mode === 'signup' ? (
          <>Already have an account? <Link to="/signin">Login</Link></>
        ) : (
          <>Don&apos;t have an account? <Link to="/signup">Sign up</Link></>
        )}
      </SwitchLine>

    </Column>
  );
}
